import { zodResolver } from '@hookform/resolvers/zod';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditNoteIcon from '@mui/icons-material/EditNote';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import ViewListIcon from '@mui/icons-material/ViewList';
import { IconButton, InputAdornment, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { green, orange, red } from '@mui/material/colors';
import { endOfDay, format, isAfter, parseISO, subDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { getUsersLatestEulaAgreementStatus } from 'api/eula';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import EulaAgreement from 'components/EulaAgreement/EulaAgreement';
import { MuiFormDatePicker, MuiFormInput, MuiFormSelect } from 'components/Form';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { StatusIdE, TicketAssigneeE } from 'declarations/enums/tickets';
import type { RelatedTicketInfoI } from 'declarations/interfaces/tickets';
import { formatTooltipWithShortcut } from 'helpers/formatTooltipWithShortcut';
import { formatInputAmount } from 'helpers/locale';
import { getCurrentUserId } from 'helpers/user';
import { useFetchTicketAttachments } from 'hooks/api/useTickets';

import { mapAgeToColor } from '../../helpers';

import AddAttachmentPopup from './components/AddAttachmentPopup/AddAttachmentPopup';
import ChangeTicketClaimStatusPopup from './components/ChangeTicketClaimStatusPopup/ChangeTicketClaimStatusPopup';
import ChangeTicketPriorityPopup from './components/ChangeTicketPriorityPopup/ChangeTicketPriorityPopup';
import DeleteTicketPopup from './components/DeleteTicketPopup/DeleteTicketPopup';
import { MessagesProvider } from './components/Messages/contexts/MessagesContext';
import Messages from './components/Messages/Messages';
import ROTicketTabs from './components/ROTicketTabs/ROTicketTabs';
import TicketApprovePopup from './components/TicketApprovePopup/TicketApprovePopup';
import TicketClosePopup from './components/TicketClosePopup/TicketClosePopup';
import TicketDetailsLoader from './components/TicketDetailsLoader';
import TicketReopenPopup from './components/TicketReopenPopup/TicketReopenPopup';
import DeleteAttachmentPopup from './components/ViewAttachmentsPopup/components/DeleteAttachmentPopup/DeleteAttachmentPopup';
import useViewAttachmentsPopup from './components/ViewAttachmentsPopup/hooks/useViewAttachmentsPopup';
import ViewAttachmentsCard from './components/ViewAttachmentsPopup/ViewAttachmentsCard';
import type { ChangeTicketDetailsFormValuesT } from './hooks/useChangeTicketDetailsForm';
import useChangeTicketDetailsForm from './hooks/useChangeTicketDetailsForm';
import useRelatedTickets from './hooks/useRelatedTickets';
import useTicketDetailsPopup from './hooks/useTicketDetailsPopup';
import {
  CardsRowFieldPriorityStyled,
  CardsRowFieldRepairDateStyled,
  CardsRowFieldStraightTimeStyled,
  FieldLabelStyled,
  FlexStyled,
  FormStyled,
  HeaderCategorySubcategoryWrapStyled,
  HeaderControlsStyled,
  HeaderFieldsAndScheduleStyled,
  HeaderInfoStyled,
  HeaderStatBlockDealerStyled,
  HeaderStatBlockStyled,
  HeaderStatBlockSubcategoryStyled,
  HeaderStatTypographyStyled,
  HeaderStyled,
  MessagesWrapStyled,
  ScheduleCardFieldStyled,
  ScheduleCardFieldsRowStyled,
  ScheduleCardStyled,
  ScheduleCardTitleStyled,
  TicketDetailBodyRowStyled,
  TicketDetailContentWrapStyled,
  TicketDetailHeaderRowWithShadowStyled,
  TicketDetailHeaderWrapStyled,
  TicketDetailLeftCardCollapsedStyled,
  TicketDetailLeftCardContentStyled,
  TicketDetailLeftCardHeaderStyled,
  TicketDetailLeftCardScrollStyled,
  TicketDetailLeftCardStyled,
  TicketDetailMessagesCardStyled,
  TicketDetailMessagesColStyled,
  TicketDetailPageWrapStyled,
  TicketDetailRightCardCollapsedStyled,
  TicketDetailRightCardContentStyled,
  TicketDetailRightCardHeaderStyled,
  TicketDetailRightCardScrollStyled,
  TicketDetailRightCardStyled,
  getPopupContentStyle,
} from './TicketDetailsPopup.styled';
import validationSchema from './validationSchema';

export type TicketDetailsPopupPropsT = {
  usePageLayout?: boolean;
  onClose?: () => void;
};

const TicketDetailsPopup: FC<TicketDetailsPopupPropsT> = ({
  usePageLayout = false,
  onClose: _onClose,
}): JSX.Element => {
  const { isMobile, isDesktop } = useResponsive();
  const {
    ticketDetails,
    isLoadingTicketDetails,
    isTicketDetailsFetchingError,
    isTicketReopenPopupOpened,
    handleOpenTicketReopenPopup,
    handleCloseTicketReopenPopup,
    handleCloseTicketDetailsPopup,
    isTicketApprovePopupOpened,
    handleOpenTicketApprovePopup,
    handleCloseTicketApprovePopup,
    isTicketClosePopupOpened,
    handleOpenTicketClosePopup,
    handleCloseTicketClosePopup,
    isAddAttachmentPopupOpened,
    handleOpenAddAttachmentPopup,
    handleCloseAddAttachmentPopup,
    selectedTicketId,
    isChangeClaimStatusPopupOpened,
    handleCloseClaimStatusChangePopup,
    isChangePriorityPopupOpened,
    handleClosePriorityChangePopup,
    isDeleteTicketPopupOpened,
    handleCloseDeleteTicketPopup,
    handleTabChange,
  } = useTicketDetailsPopup();

  const {
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions,
    ticketSubcategoriesOptions,
    isLoadingTicketSubcategoriesOptions,
    handleChangeTicketDetails,
    handleAmountBlur,
  } = useChangeTicketDetailsForm();

  const ticketIdForAttachments = selectedTicketId ?? ticketDetails?.id ?? '';
  const { data: attachments } = useFetchTicketAttachments({
    ticketId: ticketIdForAttachments,
    shouldFetch: !!ticketIdForAttachments,
  });
  const attachmentCount = attachments?.length ?? 0;

  const { relatedTickets, isLoadingRelatedTickets } = useRelatedTickets(
    selectedTicketId,
    ticketDetails?.relatedTicketIds
  );

  const currentTicketInfo: RelatedTicketInfoI | null = useMemo(
    (): RelatedTicketInfoI | null =>
      ticketDetails
        ? {
            id: ticketDetails.id,
            number: ticketDetails.number,
            lineNumber: ticketDetails.lineNumber,
            statusId: ticketDetails.statusId,
            amount: ticketDetails.amount,
            assigneeName: ticketDetails.assigneeName,
            age: ticketDetails.age,
          }
        : null,
    [ticketDetails]
  );

  const hasRelatedTickets =
    !isLoadingRelatedTickets && relatedTickets.length > 0 && currentTicketInfo !== null;

  const [assignToWarrCloud, setAssignToWarrCloud] = useState<boolean>(true);

  const handleAssignToWarrCloudChange = (value: boolean): void => {
    setAssignToWarrCloud(value);
  };

  const datePickerMaxDate = endOfDay(new Date());
  const [displayEulaAgreement, setDisplayEulaAgreement] = useState(false);
  const [isDetailsCardExpanded, setIsDetailsCardExpanded] = useState(!isMobile);
  const [isAttachmentsCardExpanded, setIsAttachmentsCardExpanded] = useState(false);

  const {
    attachments: attachmentsFromHook,
    isLoadingAttachments: isLoadingAttachmentsFromHook,
    isDeleteAttachmentPopupOpened,
    selectedAttachment,
    handleOpenDeleteAttachmentPopup,
    handleCloseDeleteAttachmentPopup,
    handleAttachmentClick,
    isLoadingAttachmentForPreview,
    isLoadingAttachmentForDownload,
    isAttachmentForDownloadError,
    isAttachmentForPreviewError,
  } = useViewAttachmentsPopup({
    ticketId: ticketIdForAttachments,
  });
  const userId = getCurrentUserId();

  useEffect(() => {
    async function checkEulaAgreement(): Promise<void> {
      if (isTicketDetailsFetchingError && userId) {
        try {
          const statusResponse = await getUsersLatestEulaAgreementStatus(userId);
          setDisplayEulaAgreement(!statusResponse);
        } catch {
          // Error fetching EULA agreement
        }
      }
    }

    checkEulaAgreement();
  }, [isTicketDetailsFetchingError, userId]);

  const handleSubmitTicketDetailsForm = useCallback(
    (data: ChangeTicketDetailsFormValuesT): void => {
      handleChangeTicketDetails({
        ...data,
        dmsScheduleAccountNumber: ticketDetails?.dmsScheduleAccountNumber ?? '',
      });
    },
    [handleChangeTicketDetails, ticketDetails]
  );

  const formMethods = useForm<ChangeTicketDetailsFormValuesT>({
    defaultValues: {
      ticketId: ticketDetails?.id,
      amount: ticketDetails?.amount.toString(),
      claimStatusId: ticketDetails?.claimStatusId,
      claimSubmittedDate: ticketDetails?.claimSubmittedDate
        ? new Date(ticketDetails?.claimSubmittedDate)
        : null,
      advisorNumber: ticketDetails?.advisorNumber,
      techNumber: ticketDetails?.techNumber,
      ticketPriorityId: ticketDetails?.ticketPriorityId,
      subCategoryId: ticketDetails?.subCategoryId,
      dmsScheduleAccountNumber: ticketDetails?.dmsScheduleAccountNumber || '',
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const { handleSubmit, setValue, clearErrors } = formMethods;

  const handleSubCategoryChange = useCallback((): void => {
    clearErrors('amount');
  }, [clearErrors]);

  useEffect((): void => {
    if (ticketDetails) {
      setValue('ticketId', ticketDetails.id);
      setValue('amount', formatInputAmount(ticketDetails.amount) ?? '0.00');
      setValue('claimStatusId', ticketDetails.claimStatusId);
      setValue('advisorNumber', ticketDetails.advisorNumber);
      setValue('techNumber', ticketDetails.techNumber);
      setValue('ticketPriorityId', ticketDetails.ticketPriorityId);
      setValue('subCategoryId', ticketDetails.subCategoryId ? ticketDetails.subCategoryId : null);
      setValue('dmsScheduleAccountNumber', ticketDetails.dmsScheduleAccountNumber || '');
      const claimSubmittedDate = ticketDetails?.claimSubmittedDate
        ? utcToZonedTime((ticketDetails?.claimSubmittedDate + 'Z') as unknown as Date, 'UTC')
        : null;

      setValue('claimSubmittedDate', claimSubmittedDate);
    }
  }, [ticketDetails, setValue]);

  useEffect((): void => {
    clearErrors();
  }, [ticketDetails, clearErrors]);

  useEffect((): void => {
    if (ticketDetails && ticketSubcategoriesOptions.length > 0) {
      const savedSubCategoryId =
        ticketDetails.requestSubCategoryId ?? ticketDetails.subCategoryId ?? null;
      if (savedSubCategoryId != null) {
        setValue('subCategoryId', savedSubCategoryId);
      }
    }
  }, [ticketDetails, ticketSubcategoriesOptions, setValue]);

  const ticketPrioritiesOptionsWithIcon: MuiFormSelectOptionT[] = useMemo(() => {
    const labelToIconAndColor: Record<
      string,
      { icon: React.ReactNode; sxProps: { color: string } }
    > = {
      Critical: { icon: <KeyboardDoubleArrowUp key="critical" />, sxProps: { color: red[900] } },
      High: { icon: <KeyboardArrowUp key="high" />, sxProps: { color: red[500] } },
      Medium: { icon: <KeyboardArrowDown key="medium" />, sxProps: { color: orange[500] } },
      Low: { icon: <KeyboardDoubleArrowDown key="low" />, sxProps: { color: green[500] } },
    };
    return ticketPrioritiesOptions.map(opt => {
      const mapped = labelToIconAndColor[opt.label];
      return {
        ...opt,
        icon: mapped?.icon ?? opt.label,
        sxProps: mapped?.sxProps,
      };
    });
  }, [ticketPrioritiesOptions]);

  const currentDate = Date.now();
  const ticketCreationDate = subDays(currentDate, ticketDetails?.age ?? 0);
  const checkIsDateAfter = (date: string): boolean => {
    return isAfter(new Date(date), new Date('1980-01-01'));
  };

  const isScheduleCategory = ticketDetails?.categoryName?.toLowerCase() === 'schedule';

  const roTicketTabs =
    hasRelatedTickets && currentTicketInfo ? (
      <ROTicketTabs
        currentTicketId={currentTicketInfo.id}
        currentTicketInfo={currentTicketInfo}
        relatedTickets={relatedTickets}
        onTabChange={handleTabChange}
        isLoading={isLoadingRelatedTickets}
      />
    ) : null;

  const ticketHeaderInner =
    ticketDetails && !isLoadingTicketDetails ? (
      <>
        {roTicketTabs}
        <TicketDetailHeaderRowWithShadowStyled>
          <HeaderStyled>
            <HeaderInfoStyled>
              <HeaderStatBlockStyled>
                <FieldLabelStyled>Ticket #</FieldLabelStyled>
                <HeaderStatTypographyStyled color="primaryText">
                  {ticketDetails.number}
                </HeaderStatTypographyStyled>
              </HeaderStatBlockStyled>
              <HeaderStatBlockStyled>
                <FieldLabelStyled>RO #</FieldLabelStyled>
                <HeaderStatTypographyStyled color="primaryText">
                  {ticketDetails.roNumber}
                </HeaderStatTypographyStyled>
              </HeaderStatBlockStyled>
              <HeaderStatBlockStyled>
                <FieldLabelStyled>Line #</FieldLabelStyled>
                <HeaderStatTypographyStyled color="primaryText">
                  {ticketDetails.lineNumber}
                </HeaderStatTypographyStyled>
              </HeaderStatBlockStyled>
              <HeaderStatBlockStyled>
                <FieldLabelStyled>VIN</FieldLabelStyled>
                <HeaderStatTypographyStyled color="primaryText">
                  {ticketDetails?.vin ?? '-'}
                </HeaderStatTypographyStyled>
              </HeaderStatBlockStyled>
              {ticketDetails?.roDate && checkIsDateAfter(ticketDetails?.roDate) && (
                <HeaderStatBlockStyled>
                  <FieldLabelStyled>Opened</FieldLabelStyled>
                  <HeaderStatTypographyStyled color="primaryText">
                    {checkIsDateAfter(ticketDetails?.roDate)
                      ? format(parseISO(ticketDetails.roDate), 'MMM do, yyyy')
                      : null}
                  </HeaderStatTypographyStyled>
                </HeaderStatBlockStyled>
              )}
              <HeaderStatBlockDealerStyled>
                <FieldLabelStyled>Dealer</FieldLabelStyled>
                <HeaderStatTypographyStyled color="primaryText">
                  {ticketDetails.roofTop.name}
                </HeaderStatTypographyStyled>
              </HeaderStatBlockDealerStyled>
              <HeaderStatBlockStyled>
                <FieldLabelStyled>Age</FieldLabelStyled>
                <HeaderStatTypographyStyled color={mapAgeToColor(ticketDetails.age)}>
                  {ticketDetails.age}
                </HeaderStatTypographyStyled>
              </HeaderStatBlockStyled>
              <HeaderStatBlockStyled>
                <FieldLabelStyled>Status</FieldLabelStyled>
                <HeaderStatTypographyStyled color="primaryText">
                  {ticketDetails.statusId}
                </HeaderStatTypographyStyled>
              </HeaderStatBlockStyled>
              {ticketDetails.closedInHours ? (
                <HeaderStatBlockStyled>
                  <FieldLabelStyled>Closed in</FieldLabelStyled>
                  <HeaderStatTypographyStyled color="primaryText">
                    {ticketDetails.closedInHours} h
                  </HeaderStatTypographyStyled>
                </HeaderStatBlockStyled>
              ) : null}
              <HeaderStatBlockStyled>
                <FieldLabelStyled>Assignee</FieldLabelStyled>
                <HeaderStatTypographyStyled
                  color={
                    ticketDetails.assignee.typeId === TicketAssigneeE.WarrCloud
                      ? 'purple'
                      : 'primaryText'
                  }
                >
                  {ticketDetails.assigneeName === 'OEM' ? 'OEM' : ticketDetails.assignee.typeId}
                </HeaderStatTypographyStyled>
              </HeaderStatBlockStyled>
              {!isMobile && (
                <HeaderCategorySubcategoryWrapStyled>
                  <HeaderStatBlockSubcategoryStyled>
                    <FieldLabelStyled>Category</FieldLabelStyled>
                    <HeaderStatTypographyStyled color="primaryText">
                      {ticketDetails.categoryName}
                    </HeaderStatTypographyStyled>
                  </HeaderStatBlockSubcategoryStyled>
                  {isScheduleCategory && (
                    <HeaderStatBlockSubcategoryStyled>
                      <FieldLabelStyled>Subcategory</FieldLabelStyled>
                      <HeaderStatTypographyStyled color="primaryText">
                        {ticketDetails.subCategory ?? ticketDetails.requestSubCategoryName ?? '-'}
                      </HeaderStatTypographyStyled>
                    </HeaderStatBlockSubcategoryStyled>
                  )}
                </HeaderCategorySubcategoryWrapStyled>
              )}
              {ticketDetails.dmsScheduleAccountNumber && (
                <HeaderStatBlockStyled>
                  <FieldLabelStyled>Account</FieldLabelStyled>
                  <HeaderStatTypographyStyled color="primaryText">
                    {ticketDetails.dmsScheduleAccountNumber}
                  </HeaderStatTypographyStyled>
                </HeaderStatBlockStyled>
              )}
            </HeaderInfoStyled>
            {!usePageLayout && (
              <HeaderFieldsAndScheduleStyled>
                <CardsRowFieldStraightTimeStyled>
                  <MuiFormInput
                    label="Amount"
                    name="amount"
                    placeholder="0.00"
                    type="number"
                    disabled={isScheduleCategory}
                    onBlur={handleAmountBlur(setValue)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </CardsRowFieldStraightTimeStyled>
                {!isScheduleCategory && (
                  <>
                    <CardsRowFieldRepairDateStyled>
                      <MuiFormSelect
                        label="Claim Status"
                        name="claimStatusId"
                        placeholder="Claim statuses"
                        options={claimStatusesOptions}
                        isLoading={isLoadingClaimStatusesOptions}
                      />
                    </CardsRowFieldRepairDateStyled>
                    <CardsRowFieldRepairDateStyled>
                      <MuiFormDatePicker
                        label="Claim Submitted Date"
                        name="claimSubmittedDate"
                        placeholder=""
                        maxDate={datePickerMaxDate}
                        disabled={isScheduleCategory}
                      />
                    </CardsRowFieldRepairDateStyled>
                    <CardsRowFieldStraightTimeStyled>
                      <MuiFormInput label="Advisor" placeholder="Advisor" name="advisorNumber" />
                    </CardsRowFieldStraightTimeStyled>
                    <CardsRowFieldStraightTimeStyled>
                      <MuiFormInput label="Tech" name="techNumber" placeholder="Tech" />
                    </CardsRowFieldStraightTimeStyled>
                    <CardsRowFieldPriorityStyled>
                      <MuiFormSelect
                        label="Priority"
                        name="ticketPriorityId"
                        placeholder="Priority"
                        options={ticketPrioritiesOptionsWithIcon}
                        isLoading={isLoadingTicketPrioritiesOptions}
                        renderSelectedValue={(opt): React.ReactNode => (
                          <Box display="flex" alignItems="center" sx={opt.sxProps ?? undefined}>
                            {opt.icon}
                          </Box>
                        )}
                        sx={{
                          '& .MuiSelect-select': {
                            paddingRight: '32px !important',
                            paddingLeft: '14px !important',
                            paddingTop: '6px !important',
                            paddingBottom: '6px !important',
                            display: 'flex',
                            alignItems: 'center',
                          },
                          '& .MuiOutlinedInput-root': { height: 'auto' },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        }}
                      />
                    </CardsRowFieldPriorityStyled>
                  </>
                )}
                {isScheduleCategory && (
                  <CardsRowFieldStraightTimeStyled>
                    <MuiFormSelect
                      label="Subcategory"
                      name="subCategoryId"
                      placeholder="Subcategories"
                      options={ticketSubcategoriesOptions}
                      isLoading={isLoadingTicketSubcategoriesOptions}
                      searchable
                      validateOnChange={false}
                      onChange={handleSubCategoryChange}
                    />
                  </CardsRowFieldStraightTimeStyled>
                )}
                {isScheduleCategory && (
                  <ScheduleCardStyled>
                    <ScheduleCardTitleStyled>Schedule</ScheduleCardTitleStyled>
                    <ScheduleCardFieldsRowStyled>
                      {ticketDetails.roClosedDate != null && (
                        <ScheduleCardFieldStyled>
                          <FieldLabelStyled>Closed RO date</FieldLabelStyled>
                          <span>
                            {checkIsDateAfter(ticketDetails.roClosedDate)
                              ? format(new Date(ticketDetails.roClosedDate), 'MMM do, yyyy')
                              : '--'}
                          </span>
                        </ScheduleCardFieldStyled>
                      )}
                      {ticketDetails.closedROAge != null && (
                        <ScheduleCardFieldStyled>
                          <FieldLabelStyled>Closed RO age</FieldLabelStyled>
                          <span>{ticketDetails.closedROAge}</span>
                        </ScheduleCardFieldStyled>
                      )}
                      <ScheduleCardFieldStyled>
                        <FieldLabelStyled>Last updated</FieldLabelStyled>
                        <span>
                          {checkIsDateAfter(ticketDetails?.lastUpdatedDateTime)
                            ? format(new Date(ticketDetails.lastUpdatedDateTime), 'MMM do, yyyy')
                            : format(new Date(ticketCreationDate), 'MMM do, yyyy')}
                        </span>
                      </ScheduleCardFieldStyled>
                    </ScheduleCardFieldsRowStyled>
                  </ScheduleCardStyled>
                )}
              </HeaderFieldsAndScheduleStyled>
            )}
            <HeaderControlsStyled>
              <Tooltip title={formatTooltipWithShortcut('Get approval')} arrow>
                <span>
                  <IconButton
                    aria-label="Get approval"
                    onClick={handleOpenTicketApprovePopup}
                    disabled={
                      !ticketDetails.isApprovalRequested || ticketDetails.isApprovedByDealer
                    }
                    sx={{ color: (t): string => t.palette.primary.main }}
                  >
                    <CheckCircleOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  ticketDetails.statusId === StatusIdE.Open ||
                  ticketDetails.statusId === StatusIdE.ReOpened
                    ? formatTooltipWithShortcut('Close Ticket')
                    : formatTooltipWithShortcut('Reopen Ticket')
                }
                arrow
              >
                <IconButton
                  aria-label={
                    ticketDetails.statusId === StatusIdE.Open ||
                    ticketDetails.statusId === StatusIdE.ReOpened
                      ? 'Close ticket'
                      : 'Reopen ticket'
                  }
                  onClick={
                    ticketDetails.statusId === StatusIdE.Open ||
                    ticketDetails.statusId === StatusIdE.ReOpened
                      ? handleOpenTicketClosePopup
                      : handleOpenTicketReopenPopup
                  }
                  sx={{
                    color: (t): string =>
                      ticketDetails.statusId === StatusIdE.Open ||
                      ticketDetails.statusId === StatusIdE.ReOpened
                        ? red[600]
                        : t.palette.primary.main,
                  }}
                >
                  {ticketDetails.statusId === StatusIdE.Open ||
                  ticketDetails.statusId === StatusIdE.ReOpened ? (
                    <RemoveCircleOutlineIcon />
                  ) : (
                    <AddCircleOutlineIcon />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={formatTooltipWithShortcut('Save')} arrow>
                <IconButton
                  type="submit"
                  aria-label="Save"
                  sx={{ color: (t): string => t.palette.primary.main }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
            </HeaderControlsStyled>
          </HeaderStyled>
        </TicketDetailHeaderRowWithShadowStyled>
      </>
    ) : null;

  const ticketHeader =
    !usePageLayout && ticketHeaderInner ? (
      <FormProvider {...formMethods}>
        <FormStyled role="form" onSubmit={handleSubmit(handleSubmitTicketDetailsForm)}>
          {ticketHeaderInner}
        </FormStyled>
      </FormProvider>
    ) : null;

  const ticketMessagesContent =
    ticketDetails &&
    !isLoadingTicketDetails &&
    (usePageLayout ? (
      <TicketDetailMessagesCardStyled>
        <Messages
          requiresAttachment={ticketDetails.requiresAttachment}
          roofTopName={ticketDetails.roofTop.name}
          handleOpenAddAttachmentPopup={handleOpenAddAttachmentPopup}
          onAssignToWarrCloudChanged={handleAssignToWarrCloudChange}
        />
      </TicketDetailMessagesCardStyled>
    ) : (
      <MessagesWrapStyled
        style={{
          height: ticketDetails.categoryName.toLowerCase() === 'schedule' ? 650 : 485,
        }}
      >
        <Messages
          requiresAttachment={ticketDetails.requiresAttachment}
          roofTopName={ticketDetails.roofTop.name}
          handleOpenAddAttachmentPopup={handleOpenAddAttachmentPopup}
          onAssignToWarrCloudChanged={handleAssignToWarrCloudChange}
        />
      </MessagesWrapStyled>
    ));

  const attachmentsCard = (
    <TicketDetailRightCardStyled $expanded={isAttachmentsCardExpanded}>
      <TicketDetailRightCardHeaderStyled>
        {isAttachmentsCardExpanded && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600 }}>
              Attachments
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '14px', fontWeight: 600, color: 'success.main' }}
            >
              ({attachmentCount})
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={isAttachmentsCardExpanded ? 'Collapse' : 'Expand'} arrow>
            <IconButton
              aria-label={isAttachmentsCardExpanded ? 'Collapse' : 'Expand'}
              onClick={(): void => setIsAttachmentsCardExpanded(!isAttachmentsCardExpanded)}
              sx={{ color: (t): string => t.palette.primary.main }}
            >
              {isAttachmentsCardExpanded ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </TicketDetailRightCardHeaderStyled>
      {!isAttachmentsCardExpanded && (
        <TicketDetailRightCardCollapsedStyled>
          <Tooltip title={`Attachments (${attachmentCount})`} arrow placement="left">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <ViewListIcon
                sx={{
                  color: (t): string => t.palette.primary.main,
                  fontSize: 28,
                }}
              />
              <Typography
                component="span"
                sx={{ fontSize: '14px', fontWeight: 600, color: 'success.main' }}
              >
                ({attachmentCount})
              </Typography>
            </Box>
          </Tooltip>
        </TicketDetailRightCardCollapsedStyled>
      )}
      {isAttachmentsCardExpanded && (
        <TicketDetailRightCardContentStyled>
          <TicketDetailRightCardScrollStyled>
            <ViewAttachmentsCard
              ticketId={ticketIdForAttachments}
              ticketStatus={ticketDetails?.statusId as StatusIdE}
              isExpanded={isAttachmentsCardExpanded}
              attachments={attachmentsFromHook}
              isLoadingAttachments={isLoadingAttachmentsFromHook}
              handleOpenDeleteAttachmentPopup={handleOpenDeleteAttachmentPopup}
              handleCloseDeleteAttachmentPopup={handleCloseDeleteAttachmentPopup}
              selectedAttachment={selectedAttachment}
              handleAttachmentClick={handleAttachmentClick}
              isLoadingAttachmentForPreview={isLoadingAttachmentForPreview}
              isLoadingAttachmentForDownload={isLoadingAttachmentForDownload}
              isAttachmentForDownloadError={isAttachmentForDownloadError}
              isAttachmentForPreviewError={isAttachmentForPreviewError}
              isDeleteAttachmentPopupOpened={isDeleteAttachmentPopupOpened}
            />
          </TicketDetailRightCardScrollStyled>
        </TicketDetailRightCardContentStyled>
      )}
    </TicketDetailRightCardStyled>
  );

  const ticketBodyContent = (
    <TicketDetailBodyRowStyled>
      <TicketDetailMessagesColStyled>{ticketMessagesContent}</TicketDetailMessagesColStyled>
      {attachmentsCard}
    </TicketDetailBodyRowStyled>
  );
  const ticketBody = ticketDetails && !isLoadingTicketDetails ? ticketBodyContent : null;

  const content = (
    <>
      {usePageLayout ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <FormProvider {...formMethods}>
            <FormStyled
              role="form"
              onSubmit={handleSubmit(handleSubmitTicketDetailsForm)}
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <TicketDetailPageWrapStyled>
                {ticketHeaderInner && (
                  <TicketDetailHeaderWrapStyled>{ticketHeaderInner}</TicketDetailHeaderWrapStyled>
                )}
                <TicketDetailContentWrapStyled>
                  <TicketDetailBodyRowStyled>
                    <TicketDetailLeftCardStyled $expanded={isDetailsCardExpanded}>
                      {isLoadingTicketDetails ? (
                        <TicketDetailsLoader variant="details" />
                      ) : (
                        <>
                          <TicketDetailLeftCardHeaderStyled>
                            {isDetailsCardExpanded && (
                              <span style={{ fontWeight: 600, fontSize: '14px' }}>Details</span>
                            )}
                            <Tooltip title={isDetailsCardExpanded ? 'Collapse' : 'Expand'} arrow>
                              <IconButton
                                aria-label={isDetailsCardExpanded ? 'Collapse' : 'Expand'}
                                onClick={(): void =>
                                  setIsDetailsCardExpanded(!isDetailsCardExpanded)
                                }
                                sx={{ color: (t): string => t.palette.primary.main }}
                              >
                                {isDetailsCardExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                              </IconButton>
                            </Tooltip>
                          </TicketDetailLeftCardHeaderStyled>
                          {!isDetailsCardExpanded && (
                            <TicketDetailLeftCardCollapsedStyled>
                              <Tooltip title="Details" arrow placement="right">
                                <EditNoteIcon
                                  sx={{
                                    color: (t): string => t.palette.primary.main,
                                    fontSize: 28,
                                  }}
                                />
                              </Tooltip>
                            </TicketDetailLeftCardCollapsedStyled>
                          )}
                          {isDetailsCardExpanded && ticketDetails && (
                            <TicketDetailLeftCardContentStyled>
                              <TicketDetailLeftCardScrollStyled>
                                {isScheduleCategory && (
                                  <ScheduleCardStyled>
                                    <ScheduleCardTitleStyled>Schedule</ScheduleCardTitleStyled>
                                    <ScheduleCardFieldsRowStyled>
                                      {ticketDetails.roClosedDate != null && (
                                        <ScheduleCardFieldStyled>
                                          <FieldLabelStyled>Closed RO date</FieldLabelStyled>
                                          <span>
                                            {checkIsDateAfter(ticketDetails.roClosedDate)
                                              ? format(
                                                  new Date(ticketDetails.roClosedDate),
                                                  'MMM do, yyyy'
                                                )
                                              : '--'}
                                          </span>
                                        </ScheduleCardFieldStyled>
                                      )}
                                      {ticketDetails.closedROAge != null && (
                                        <ScheduleCardFieldStyled>
                                          <FieldLabelStyled>Closed RO age</FieldLabelStyled>
                                          <span>{ticketDetails.closedROAge}</span>
                                        </ScheduleCardFieldStyled>
                                      )}
                                      <ScheduleCardFieldStyled>
                                        <FieldLabelStyled>Last updated</FieldLabelStyled>
                                        <span>
                                          {checkIsDateAfter(ticketDetails?.lastUpdatedDateTime)
                                            ? format(
                                                new Date(ticketDetails.lastUpdatedDateTime),
                                                'MMM do, yyyy'
                                              )
                                            : format(new Date(ticketCreationDate), 'MMM do, yyyy')}
                                        </span>
                                      </ScheduleCardFieldStyled>
                                    </ScheduleCardFieldsRowStyled>
                                  </ScheduleCardStyled>
                                )}
                                <CardsRowFieldStraightTimeStyled>
                                  <MuiFormInput
                                    label="Amount"
                                    name="amount"
                                    placeholder="0.00"
                                    type="number"
                                    disabled={isScheduleCategory}
                                    onBlur={handleAmountBlur(setValue)}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <AttachMoneyIcon fontSize="small" />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </CardsRowFieldStraightTimeStyled>
                                {!isScheduleCategory && (
                                  <>
                                    <CardsRowFieldRepairDateStyled>
                                      <MuiFormSelect
                                        label="Claim Status"
                                        name="claimStatusId"
                                        placeholder="Claim statuses"
                                        options={claimStatusesOptions}
                                        isLoading={isLoadingClaimStatusesOptions}
                                      />
                                    </CardsRowFieldRepairDateStyled>
                                    <CardsRowFieldRepairDateStyled>
                                      <MuiFormDatePicker
                                        label="Claim Submitted Date"
                                        name="claimSubmittedDate"
                                        placeholder=""
                                        maxDate={datePickerMaxDate}
                                        disabled={isScheduleCategory}
                                      />
                                    </CardsRowFieldRepairDateStyled>
                                    <CardsRowFieldStraightTimeStyled>
                                      <MuiFormInput
                                        label="Advisor"
                                        placeholder="Advisor"
                                        name="advisorNumber"
                                      />
                                    </CardsRowFieldStraightTimeStyled>
                                    <CardsRowFieldStraightTimeStyled>
                                      <MuiFormInput
                                        label="Tech"
                                        name="techNumber"
                                        placeholder="Tech"
                                      />
                                    </CardsRowFieldStraightTimeStyled>
                                    <CardsRowFieldPriorityStyled>
                                      <MuiFormSelect
                                        label="Priority"
                                        name="ticketPriorityId"
                                        placeholder="Priority"
                                        options={ticketPrioritiesOptionsWithIcon}
                                        isLoading={isLoadingTicketPrioritiesOptions}
                                        renderSelectedValue={(opt): React.ReactNode => (
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            sx={opt.sxProps ?? undefined}
                                          >
                                            {opt.icon}
                                          </Box>
                                        )}
                                        sx={{
                                          '& .MuiSelect-select': {
                                            paddingRight: '32px !important',
                                            paddingLeft: '14px !important',
                                            paddingTop: '6px !important',
                                            paddingBottom: '6px !important',
                                            display: 'flex',
                                            alignItems: 'center',
                                          },
                                          '& .MuiOutlinedInput-root': { height: 'auto' },
                                          '& .MuiInputLabel-shrink': {
                                            transform: 'translate(14px, -6px) scale(0.75)',
                                          },
                                        }}
                                      />
                                    </CardsRowFieldPriorityStyled>
                                  </>
                                )}
                                {isScheduleCategory && (
                                  <CardsRowFieldStraightTimeStyled>
                                    <MuiFormSelect
                                      label="Subcategory"
                                      name="subCategoryId"
                                      placeholder="Subcategories"
                                      options={ticketSubcategoriesOptions}
                                      isLoading={isLoadingTicketSubcategoriesOptions}
                                      searchable
                                      validateOnChange={false}
                                      onChange={handleSubCategoryChange}
                                    />
                                  </CardsRowFieldStraightTimeStyled>
                                )}
                              </TicketDetailLeftCardScrollStyled>
                            </TicketDetailLeftCardContentStyled>
                          )}
                        </>
                      )}
                    </TicketDetailLeftCardStyled>
                    <TicketDetailMessagesColStyled>
                      {isLoadingTicketDetails ? (
                        <TicketDetailMessagesCardStyled>
                          <TicketDetailsLoader variant="messages" />
                        </TicketDetailMessagesCardStyled>
                      ) : (
                        ticketMessagesContent
                      )}
                    </TicketDetailMessagesColStyled>
                    {isLoadingTicketDetails ? (
                      <TicketDetailRightCardStyled $expanded={isAttachmentsCardExpanded}>
                        <TicketDetailsLoader
                          variant="attachments"
                          isAttachmentsCardCollapsed={!isAttachmentsCardExpanded}
                        />
                      </TicketDetailRightCardStyled>
                    ) : (
                      attachmentsCard
                    )}
                  </TicketDetailBodyRowStyled>
                  {isTicketDetailsFetchingError && (
                    <FlexStyled direction="column" align="center" justify="center">
                      <ErrorSection />
                    </FlexStyled>
                  )}
                  {isTicketDetailsFetchingError && displayEulaAgreement && <EulaAgreement />}
                </TicketDetailContentWrapStyled>
              </TicketDetailPageWrapStyled>
            </FormStyled>
          </FormProvider>
        </div>
      ) : (
        <>
          {ticketHeader}
          {ticketBody}
          {isLoadingTicketDetails && <TicketDetailsLoader />}
          {isTicketDetailsFetchingError && (
            <FlexStyled direction="column" align="center" justify="center">
              <ErrorSection />
            </FlexStyled>
          )}
          {isTicketDetailsFetchingError && displayEulaAgreement && <EulaAgreement />}
        </>
      )}

      <TicketReopenPopup
        ticketId={ticketDetails?.id || ''}
        isOpen={isTicketReopenPopupOpened}
        onClose={handleCloseTicketReopenPopup}
      />
      <TicketApprovePopup
        ticketId={ticketDetails?.id || ''}
        isOpen={isTicketApprovePopupOpened}
        onClose={handleCloseTicketApprovePopup}
      />
      <TicketClosePopup
        ticketId={ticketDetails?.id || ''}
        isOpen={isTicketClosePopupOpened}
        onClose={handleCloseTicketClosePopup}
      />
      <AddAttachmentPopup
        ticketId={ticketDetails?.id || ''}
        categoryName={ticketDetails?.categoryName || ''}
        isOpen={isAddAttachmentPopupOpened}
        assignToWarrCloud={assignToWarrCloud}
        onClose={handleCloseAddAttachmentPopup}
      />
      <ChangeTicketClaimStatusPopup
        ticketId={ticketDetails?.id || ''}
        claimStatusId={ticketDetails?.claimStatusId || 0}
        isOpen={isChangeClaimStatusPopupOpened}
        onClose={handleCloseClaimStatusChangePopup}
      />
      <ChangeTicketPriorityPopup
        ticketId={ticketDetails?.id || ''}
        ticketPriorityId={ticketDetails?.ticketPriorityId || 0}
        isOpen={isChangePriorityPopupOpened}
        onClose={handleClosePriorityChangePopup}
      />
      <DeleteTicketPopup
        ticketId={ticketDetails?.id || ''}
        isOpen={isDeleteTicketPopupOpened}
        onClose={handleCloseDeleteTicketPopup}
      />
      <DeleteAttachmentPopup
        ticketId={ticketIdForAttachments}
        fileName={selectedAttachment?.fileName || ''}
        originalName={
          (selectedAttachment?.originalName !== undefined
            ? selectedAttachment?.originalName
            : selectedAttachment?.fileName) || ''
        }
        isOpen={isDeleteAttachmentPopupOpened}
        onClose={handleCloseDeleteAttachmentPopup}
      />
    </>
  );

  if (usePageLayout) {
    return <>{content}</>;
  }

  return (
    <Popup
      nested
      popupId={ScrollLockIdE.TicketDetailsPopup}
      contentStyle={getPopupContentStyle(isMobile, isDesktop)}
      isOpen={!!selectedTicketId}
      onClose={handleCloseTicketDetailsPopup}
      showCloseBtn={isLoadingTicketDetails || !!isTicketDetailsFetchingError}
      closeOnEscape={false}
      closeOnDocumentClick={false}
    >
      {content}
    </Popup>
  );
};

export { TicketDetailsPopup };

const TicketDetailsPopupWithMessagesProvider: FC = (): JSX.Element => {
  const { selectedTicketId } = useTickets();
  const ticketId = selectedTicketId || '';

  return (
    <MessagesProvider ticketId={ticketId}>
      <TicketDetailsPopup />
    </MessagesProvider>
  );
};

export default TicketDetailsPopupWithMessagesProvider;
